import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';

import { Typography, Button, Box } from '@mui/material';

import { ApiKeyInfo, ApiKeyStatsDay } from 'common/types';
import {
	getShopApiKeysListener,
	getShopApiKeysStatsListener,
	deleteApiKey,
} from 'common/api/frontend/apiKeys';
import EmptyState from 'common/components/EmptyState';

import CreateApiKeyModal from './CreateApiKeyModal';
import ApiKeysTable from './ApiKeysTable';
import ContainerBox from 'components/ContainerBox';

import * as ShopSelectors from 'selectors/ShopSelectors';
import { useTranslation } from 'services/localization';
import ExternalLinkButton from 'common/components/ExternalLinkButton';
import { API_DOCS_URL } from 'common/constants/api';
import Banner from 'components/Banner';

const ApiKeys = () => {
	const shopId = useSelector(ShopSelectors.activeShopId)!;
	const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(false);
	const [apiKeys, setApiKeys] = useState<ApiKeyInfo[]>([]);
	const [apiKeysStats, setApiKeysStats] = useState<ApiKeyStatsDay[]>([]);
	const { t } = useTranslation();

	useEffect(() => {
		const removeListener = getShopApiKeysListener(shopId, {
			onData: (data: ApiKeyInfo[]) => {
				setApiKeys(data);
				setLoading(false);
			},
			onError: (error) => {
				setError(true);
				setLoading(false);
			},
		});

		return () => {
			removeListener();
		};
	}, [shopId]);

	useEffect(() => {
		const removeListener = getShopApiKeysStatsListener(shopId, {
			onData: (data: ApiKeyStatsDay[]) => {
				setApiKeysStats(data);
			},
			onError: (error) => {
				setError(true);
			},
		});

		return () => {
			removeListener();
		};
	}, [shopId]);

	const handleDelete = async (keyId: string) => {
		await deleteApiKey(shopId, keyId);
	};

	return (
		<>
			{error && (
				<Banner
					variant="danger"
					title={t(
						'Integrations.ApiKeys.errorFetchingApiKeys',
						'An error occurred while fetching your API keys. Please refresh the page.',
					)}
				/>
			)}

			<ContainerBox padding="normal">
				<Box
					display="flex"
					flexDirection="row"
					alignItems="flex-end"
					justifyContent="space-between"
				>
					<Box>
						<Typography variant="h6" gutterBottom>
							{t('Integrations.ApiKeys.title', 'API keys')}
						</Typography>
						<Typography variant="body2">
							{t('Integrations.ApiKeys.description', 'Manage your API keys for the Rentle API.')}
						</Typography>
					</Box>
					<Button variant="outlined" color="primary" onClick={() => setIsCreateModalOpen(true)}>
						{t('Integrations.ApiKeys.createApiKey', 'Create API key')}
					</Button>
				</Box>
				<Box my={2} />
				<EmptyState
					isLoading={loading}
					isEmpty={apiKeys.length === 0}
					title={t('Integrations.ApiKeys.noApiKeys', 'You have no API keys')}
					description={t(
						'Integrations.ApiKeys.noApiKeysDescription',
						'Have some "less human" users that would need to access your shop? The Rentle API is for you. Get started with using the API by creating your first API key here.',
					)}
					render={() => (
						<ApiKeysTable data={apiKeys} stats={apiKeysStats} onDelete={handleDelete} />
					)}
				/>
				<Box display="flex" flexDirection="row" alignItems="center" justifyContent="flex-end">
					<ExternalLinkButton
						href={API_DOCS_URL}
						text={t('Integrations.apiDocumentation', 'API documentation')}
					/>
				</Box>
				<CreateApiKeyModal isOpen={isCreateModalOpen} onClose={() => setIsCreateModalOpen(false)} />
			</ContainerBox>
		</>
	);
};

export default ApiKeys;
